import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectAuthStatus } from '../features/auth/authSlice';
import { getRoleHomePath } from '../routes/roleHome';
import LogoIcon from '../assets/logo.svg';
import LogoWordmark from '../assets/logo-wordmark.svg';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Carousel from '../components/ui/Carousel';
import FadeInSection from '../components/home/FadeInSection';
import { CAROUSEL_SLIDES } from '../components/home/carouselSlides';
import { prefersReducedMotion } from '../animations/usePrefersReducedMotion';
import { formatCurrency } from '../lib/formatPlan';
import {
  ShieldCheckIcon,
  ChartLineIcon,
  WalletIcon,
  ClipboardCheckIcon,
  UserPlusIcon,
  TrendingUpIcon,
  ArrowRightIcon,
} from '../components/home/icons';

const FEATURES = [
  {
    icon: ShieldCheckIcon,
    title: 'KYC-verified security',
    body: 'No investor sees live portfolio or wallet data until their identity is verified and approved by an admin.',
  },
  {
    icon: ChartLineIcon,
    title: 'Real-time portfolio tracking',
    body: 'Capital, ROI, and growth over time — visualized the moment you log in, refreshed on a schedule so it stays fast.',
  },
  {
    icon: WalletIcon,
    title: 'Transparent wallet & payouts',
    body: 'Every interest payout lands in an in-app wallet with a full transaction history, not a black box.',
  },
  {
    icon: ClipboardCheckIcon,
    title: 'Immutable audit trail',
    body: 'Every rate change, status update, and approval is logged — who, what, when, before and after.',
  },
];

const STEPS = [
  {
    icon: UserPlusIcon,
    title: 'Get verified',
    body: 'Create an account and complete a short KYC form. Admins review and approve it before anything goes live.',
  },
  {
    icon: TrendingUpIcon,
    title: 'Choose a plan',
    body: 'An admin sets up your fixed-return investment with a defined principal, rate, and payout schedule.',
  },
  {
    icon: WalletIcon,
    title: 'Watch it grow',
    body: 'Track your portfolio, get paid into your wallet, and follow every payout with a full history.',
  },
];

const TRUST_SIGNALS = [
  { icon: ShieldCheckIcon, label: 'KYC verified' },
  { icon: ClipboardCheckIcon, label: 'Secure by design' },
  { icon: ChartLineIcon, label: 'Transparent reporting' },
];

const PLAN_PREVIEWS = [
  { name: 'Starter 3mo', roi: '8%', range: '₦50,000+', duration: '3 months' },
  { name: 'Fixed 6mo', roi: '12%', range: '₦100,000+', duration: '6 months' },
  { name: 'Growth 12mo', roi: '18%', range: '₦250,000+', duration: '12 months' },
  { name: 'Premium 24mo', roi: '24%', range: '₦500,000+', duration: '24 months' },
];

function useAuthState() {
  // App.jsx's useAuthBootstrap already resolved before Home can render —
  // reads the result from Redux instead of firing its own GET /auth/me.
  const user = useSelector(selectCurrentUser);
  const status = useSelector(selectAuthStatus);
  return { user, isAuthenticated: status === 'authenticated' };
}

function Nav() {
  const { user, isAuthenticated } = useAuthState();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen || !panelRef.current) return undefined;
    gsap.fromTo(panelRef.current, { opacity: 0, y: -8 }, { opacity: 1, y: 0, duration: 0.22, ease: 'power2.out' });
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const navLinks = [
    { href: '#carousel', label: 'Product' },
    { href: '#features', label: 'Features' },
    { href: '#how-it-works', label: 'How it works' },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-colors duration-300 ${
        scrolled || mobileOpen ? 'bg-background/90 backdrop-blur-sm border-b border-border' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
          <img src={LogoIcon} alt="" className="h-8 w-8 rounded-lg" />
          <span className="font-semibold text-foreground">Cavan Capital</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-foreground-muted">
          {navLinks.map((l) => (
            <a key={l.href} href={l.href} className="hover:text-foreground transition-colors">{l.label}</a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated ? (
            <Link to={getRoleHomePath(user?.roles)}>
              <Button size="sm">Go to dashboard</Button>
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-sm text-foreground-muted hover:text-foreground transition-colors">
                Log in
              </Link>
              <Link to="/register">
                <Button size="sm" className="accent-gradient border-0">Get Started</Button>
              </Link>
            </>
          )}
        </div>

        <button
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          className="md:hidden h-9 w-9 flex items-center justify-center rounded-lg text-foreground hover:bg-background-elevated transition-colors"
        >
          {mobileOpen ? (
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5">
              <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>

      {mobileOpen && (
        <div ref={panelRef} className="md:hidden border-t border-border bg-background px-6 py-6 space-y-6">
          <nav className="flex flex-col gap-4 text-sm text-foreground-muted">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} onClick={() => setMobileOpen(false)} className="hover:text-foreground transition-colors">
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex flex-col gap-3 pt-4 border-t border-border">
            {isAuthenticated ? (
              <Link to={getRoleHomePath(user?.roles)} onClick={() => setMobileOpen(false)}>
                <Button className="w-full">Go to dashboard</Button>
              </Link>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileOpen(false)}>
                  <Button variant="secondary" className="w-full">Log in</Button>
                </Link>
                <Link to="/register" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full accent-gradient border-0">Get Started</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

/**
 * Static-styled mockup card, not a live figure — fills the hero's empty
 * right-side space on desktop with a concrete sense of "what does the
 * product actually look like." Numbers are the illustrative example from
 * the design brief, run through the same formatCurrency the real app uses
 * so it reads visually identical to a real portfolio card.
 */
function HeroPreviewPanel({ panelRef }) {
  return (
    <div ref={panelRef} className="hidden lg:block w-[320px] xl:w-[360px] shrink-0">
      <div className="glass-card shadow-card-hover p-6">
        <div className="flex items-center justify-between mb-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle">Portfolio snapshot</p>
          <span className="inline-flex items-center gap-1.5 text-[11px] text-foreground-subtle">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            Preview
          </span>
        </div>

        <p className="text-xs text-foreground-subtle">Total invested</p>
        <p className="text-2xl font-bold text-foreground tabular-nums mt-0.5">{formatCurrency(2450000)}</p>

        <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-border">
          <div>
            <p className="text-xs text-foreground-subtle">Active investments</p>
            <p className="text-lg font-semibold text-foreground mt-0.5">3</p>
          </div>
          <div>
            <p className="text-xs text-foreground-subtle">Interest earned</p>
            <p className="text-lg font-semibold text-accent tabular-nums mt-0.5">{formatCurrency(186200)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The hero's entrance is a single GSAP timeline (not several independent
 * fromTo calls) so the stagger between elements is authored as relative
 * offsets on one timeline — badge at 0, headline lines ~130ms apart, the
 * paragraph, then the CTAs scaling in from 0.95, finishing under ~1s. The
 * background glows and the preview panel's floating drift are separate,
 * looping tweens that only start once the entrance settles. Everything is
 * skipped in favor of the final resting state under prefers-reduced-motion.
 */
function Hero() {
  const { user, isAuthenticated } = useAuthState();
  const badgeRef = useRef(null);
  const line1Ref = useRef(null);
  const line2Ref = useRef(null);
  const paraRef = useRef(null);
  const ctaRef = useRef(null);
  const previewRef = useRef(null);
  const blob1Ref = useRef(null);
  const blob2Ref = useRef(null);

  useEffect(() => {
    const nodes = [badgeRef.current, line1Ref.current, line2Ref.current, paraRef.current, ctaRef.current, previewRef.current].filter(
      Boolean
    );

    if (prefersReducedMotion()) {
      gsap.set(nodes, { opacity: 1, y: 0, scale: 1 });
      return undefined;
    }

    const ctx = gsap.context(() => {
      gsap
        .timeline()
        .fromTo(badgeRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }, 0)
        .fromTo(line1Ref.current, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.15)
        .fromTo(line2Ref.current, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, 0.28)
        .fromTo(paraRef.current, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }, 0.42)
        .fromTo(
          ctaRef.current,
          { opacity: 0, y: 12, scale: 0.95 },
          { opacity: 1, y: 0, scale: 1, duration: 0.4, ease: 'back.out(1.5)' },
          0.56
        )
        .fromTo(
          previewRef.current,
          { opacity: 0, y: 20, scale: 0.97 },
          { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'power2.out' },
          0.3
        );

      // Slow drifting background glow — one primary treatment (soft gradient
      // blobs at low opacity), not combined with a grid or line-chart motif.
      // yoyo + repeat:-1 makes each a ~16-20s round trip.
      gsap.to(blob1Ref.current, { x: 40, y: -30, duration: 9, ease: 'sine.inOut', yoyo: true, repeat: -1 });
      gsap.to(blob2Ref.current, { x: -30, y: 25, duration: 10, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 0.4 });

      // Gentle up/down float on the preview panel so it doesn't sit dead
      // still once it's finished entering — starts once the entrance timeline
      // has visually settled.
      gsap.to(previewRef.current, { y: '+=10', duration: 2.6, ease: 'sine.inOut', yoyo: true, repeat: -1, delay: 1.1 });
    });

    return () => ctx.revert();
  }, []);

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10" aria-hidden="true">
        <div ref={blob1Ref} className="absolute top-[-10%] right-[-10%] h-[420px] w-[420px] rounded-full bg-accent/10 blur-3xl" />
        <div ref={blob2Ref} className="absolute bottom-[-15%] left-[-10%] h-[380px] w-[380px] rounded-full bg-accent/5 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="flex flex-col lg:flex-row lg:items-center gap-12">
          <div className="max-w-2xl">
            <span
              ref={badgeRef}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-accent-soft text-accent border border-accent/30 mb-6"
            >
              Fixed-return investing, done transparently
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight text-balance mb-6">
              <span ref={line1Ref} className="block">Grow your wealth</span>
              <span ref={line2Ref} className="block">with a platform built for trust</span>
            </h1>
            <p ref={paraRef} className="text-lg text-foreground-muted leading-relaxed mb-8 max-w-xl">
              Cavan Capital gives you a clear, real-time view of your fixed-return investments — from KYC-verified
              onboarding to portfolio growth, payouts, and everything in between.
            </p>
            <div ref={ctaRef} className="flex flex-wrap items-center gap-4">
              {isAuthenticated ? (
                <Link to={getRoleHomePath(user?.roles)}>
                  <Button size="lg" className="accent-gradient border-0 shadow-glow group">
                    Go to your dashboard
                    <ArrowRightIcon className="transition-transform duration-200 group-hover:translate-x-1" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/register">
                    <Button size="lg" className="accent-gradient border-0 shadow-glow group">
                      Get Started
                      <ArrowRightIcon className="transition-transform duration-200 group-hover:translate-x-1" />
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button variant="secondary" size="lg">Log In</Button>
                  </Link>
                </>
              )}
            </div>
          </div>

          <HeroPreviewPanel panelRef={previewRef} />
        </div>
      </div>
    </section>
  );
}

/** The landing page's centerpiece "alive" element — auto-advancing, swipeable, real-product previews. */
function ProductCarousel() {
  const slides = CAROUSEL_SLIDES.map((s) => ({
    id: s.id,
    content: (
      <div className="glass-card p-6 sm:p-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center min-h-[280px] sm:min-h-[320px]">
        <div>
          <p className="text-accent text-xs font-semibold uppercase tracking-wide mb-2">{s.eyebrow}</p>
          <h3 className="text-2xl sm:text-3xl font-semibold text-foreground text-balance mb-3">{s.title}</h3>
          <p className="text-sm text-foreground-muted leading-relaxed">{s.body}</p>
          {s.note && <p className="text-xs text-foreground-subtle mt-3 italic">{s.note}</p>}
        </div>
        <div>{s.visual}</div>
      </div>
    ),
  }));

  return (
    <section id="carousel" className="max-w-6xl mx-auto px-6 py-16 sm:py-20">
      <FadeInSection>
        <Carousel slides={slides} />
      </FadeInSection>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="border-y border-border bg-background-surface/60">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <FadeInSection className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
          {TRUST_SIGNALS.map((t) => (
            <div key={t.label} className="flex items-center gap-2.5 text-sm text-foreground-muted">
              <span className="h-8 w-8 rounded-lg bg-accent-soft text-accent flex items-center justify-center shrink-0">
                <t.icon width={16} height={16} />
              </span>
              {t.label}
            </div>
          ))}
        </FadeInSection>
      </div>
    </section>
  );
}

// Deliberately no "numbers band" section here (total AUM, investor count,
// etc.) — there's no public, unauthenticated endpoint this page can pull
// real platform figures from, and the brief is explicit that a fabricated
// or placeholder stats band undermines the trust positioning the hero
// already promises. Add one back only when a real, citable figure exists.

function Features() {
  return (
    <section id="features" className="max-w-6xl mx-auto px-6 py-20 sm:py-28">
      <FadeInSection className="max-w-xl mb-14">
        <p className="text-accent text-sm font-semibold uppercase tracking-wide mb-3">Why Cavan Capital</p>
        <h2 className="text-3xl sm:text-4xl font-semibold text-foreground text-balance">
          Everything a serious investor needs, nothing they don't
        </h2>
      </FadeInSection>

      <div className="grid sm:grid-cols-2 gap-6">
        {FEATURES.map((f, i) => (
          <FadeInSection key={f.title} delay={i * 0.08} className="glass-card glass-card-hover p-6">
            <div className="h-11 w-11 rounded-xl bg-accent-soft text-accent flex items-center justify-center mb-4">
              <f.icon />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-2">{f.title}</h3>
            <p className="text-sm text-foreground-muted leading-relaxed">{f.body}</p>
          </FadeInSection>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-background-surface/60 border-y border-border">
      <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28">
        <FadeInSection className="max-w-xl mb-14">
          <p className="text-accent text-sm font-semibold uppercase tracking-wide mb-3">How it works</p>
          <h2 className="text-3xl sm:text-4xl font-semibold text-foreground text-balance">
            From sign-up to your first payout
          </h2>
        </FadeInSection>

        <div className="relative grid sm:grid-cols-3 gap-8">
          {/* Connecting line reinforcing the 1-2-3 sequence — sits behind the
              icon row, spans between icon centers, desktop only (the steps
              stack vertically on mobile so a horizontal line has nothing to
              connect there). */}
          <div className="hidden sm:block absolute top-[22px] left-[calc(16.66%+22px)] right-[calc(16.66%+22px)] h-px bg-gradient-to-r from-accent/40 via-accent/20 to-accent/40" aria-hidden="true" />

          {STEPS.map((step, i) => (
            <FadeInSection key={step.title} delay={i * 0.1} className="relative">
              <div className="relative z-10 h-11 w-11 rounded-xl bg-accent-soft text-accent flex items-center justify-center mb-4 ring-4 ring-background-surface">
                <step.icon />
              </div>
              <p className="text-xs font-semibold text-foreground-subtle mb-1">STEP {i + 1}</p>
              <h3 className="text-base font-semibold text-foreground mb-2">{step.title}</h3>
              <p className="text-sm text-foreground-muted leading-relaxed">{step.body}</p>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlansPreview() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-20 sm:py-28">
      <FadeInSection className="max-w-xl mb-10">
        <p className="text-accent text-sm font-semibold uppercase tracking-wide mb-3">Plan types</p>
        <h2 className="text-3xl sm:text-4xl font-semibold text-foreground text-balance">
          Fixed-return plans for every horizon
        </h2>
        <p className="text-sm text-foreground-subtle mt-3 italic">Illustrative examples — sign in to see live rates.</p>
      </FadeInSection>

      <FadeInSection className="flex gap-4 overflow-x-auto pb-2 -mx-6 px-6 sm:mx-0 sm:px-0">
        {PLAN_PREVIEWS.map((p) => (
          <div key={p.name} className="glass-card glass-card-hover p-5 shrink-0 w-56">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-foreground">{p.name}</p>
              <Badge tone="success">{p.roi} ROI</Badge>
            </div>
            <p className="text-xs text-foreground-subtle mt-3">From {p.range}</p>
            <p className="text-xs text-foreground-subtle">{p.duration}</p>
          </div>
        ))}
      </FadeInSection>
    </section>
  );
}

function CtaBand() {
  const { isAuthenticated } = useAuthState();
  if (isAuthenticated) return null;

  return (
    <section className="max-w-6xl mx-auto px-6 py-20">
      <FadeInSection className="glass-card p-10 sm:p-14 text-center bg-gradient-to-br from-background-elevated to-background-surface">
        <h2 className="text-3xl sm:text-4xl font-semibold text-foreground text-balance mb-4">
          Ready to start tracking your investments properly?
        </h2>
        <p className="text-foreground-muted max-w-xl mx-auto mb-8">
          Create your account in minutes. Verification and your first investment plan are handled by our team.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link to="/register">
            <Button size="lg" className="accent-gradient border-0 shadow-glow group">
              Get Started
              <ArrowRightIcon className="transition-transform duration-200 group-hover:translate-x-1" />
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="secondary" size="lg">I already have an account</Button>
          </Link>
        </div>
      </FadeInSection>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border">
      <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
        <img src={LogoWordmark} alt="Cavan Capital" className="h-7 w-auto" />
        <nav className="flex items-center gap-6 text-xs text-foreground-muted">
          <Link to="/login" className="hover:text-foreground transition-colors">Log in</Link>
          <Link to="/register" className="hover:text-foreground transition-colors">Get started</Link>
          <a href="#how-it-works" className="hover:text-foreground transition-colors">How it works</a>
        </nav>
        <p className="text-xs text-foreground-subtle text-center sm:text-right max-w-md">
          © {new Date().getFullYear()} Cavan Capital. Fixed-return investment tracking for the Nigerian market. This
          build is a product demo and does not currently represent a licensed financial offering.
        </p>
      </div>
    </footer>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-background">
      <Nav />
      <Hero />
      <ProductCarousel />
      <TrustStrip />
      <Features />
      <HowItWorks />
      <PlansPreview />
      <CtaBand />
      <Footer />
    </div>
  );
}
